import React, { useEffect, useMemo } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import SimpleBar from "simplebar-react";



interface Props {
  fileUrl?: string | null;
  file?: File | null;
  isOpen: boolean;
  onClose: () => void;
  size?: string;
}


const FilePreviewModal: React.FC<Props> = ({
  file,
  fileUrl,
  isOpen,
  onClose,
  size = "lg",
}) => {
  const resolvedUrl = useMemo(() => {
    if (file) return URL.createObjectURL(file);
    if (fileUrl) return fileUrl;
    return null;
  }, [file, fileUrl]);

  useEffect(() => {
    if (!file) return;

    return () => {
      if (resolvedUrl) {
        URL.revokeObjectURL(resolvedUrl);
      }
    };
  }, [file, resolvedUrl]);

  if (!resolvedUrl) return null;

  const fileName = file?.name || resolvedUrl;

  const isPdf = fileName?.toLowerCase().endsWith(".pdf");
  const isImage = file?.type?.startsWith("image") || fileName?.match(/\.(jpg|jpeg|png)$/i);
  const isDoc = fileName?.match(/\.(doc|docx)$/i);
  const isExcel = fileName?.match(/\.(xls|xlsx)$/i);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size={size} centered scrollable backdrop="static"
      className="common_modal">
      <ModalHeader toggle={onClose} className="p-3">File Preview</ModalHeader>
      <SimpleBar style={{ maxHeight: "400px" }}>
        <ModalBody className="w-100">

          {/* Image */}
          {isImage && (
            <img
              src={resolvedUrl}
              className="img-fluid rounded w-100"
              alt="Preview"
            />
          )}

          {/* PDF */}
          {isPdf && (
            <iframe
              src={`${resolvedUrl}#navpanes=0`}
              width="100%"
              height="500px"
              style={{ border: "none" }}
              title="PDF Preview"
            />
          )}

          {/* DOC / DOCX */}
          {isDoc && (
            <div className="py-5 text-center">
              <i className="ri-file-word-line text-primary" style={{ fontSize: "48px" }}></i>
              <h5 className="mt-3">{fileName || 'Word Document'}</h5>
              <p className="text-muted">Preview not available. Please download the file to view it.</p>
              <a href={resolvedUrl} download={fileName} target="_blank" rel="noreferrer" className="btn btn-primary mt-2">
                <i className="ri-download-line me-1"></i> Download File
              </a>
            </div>
          )}

          {/* XLS / XLSX */}
          {isExcel && (
            <div className="py-5 text-center">
              <i className="ri-file-excel-line text-success" style={{ fontSize: "48px" }}></i>
              <h5 className="mt-3">{fileName || 'Excel Document'}</h5>
              <p className="text-muted">Preview not available. Please download the file to view it.</p>
              <a href={resolvedUrl} download={fileName} target="_blank" rel="noreferrer" className="btn btn-primary mt-2">
                <i className="ri-download-line me-1"></i> Download File
              </a>
            </div>
          )}

        </ModalBody>
      </SimpleBar>

      <ModalFooter className="bg-white border-top">
        <Button color="secondary" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};


export default FilePreviewModal;
